import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import ComboSelect from '@atom/components/common/comboSelect/ComboSelect';
import { GET_BUDGET_CATEGORY_FILTERS } from '@atom/graph/budget';
import {
  BudgetCategoriesFilterConnectionInput,
  BudgetCategory,
  BudgetCategoryFiltersConnection,
} from '@atom/types/budget';
import { objectIdsMatch } from '@atom/utilities/objectCompareUtilities';

import BudgetDetailContext from '../BudgetDetailContext';
import { DEBOUNCE_TIME } from '../budgetDetailUtils';
import { FILTER_KEYS, useBudgetFilterStorage } from '../useBudgetFilterStorage';

const LIMIT = 25;
const MIN_SEARCH_CHARS = 2;

const BudgetDetailCategoryFilter = () => {
  const {
    categoryFilters,
    setCategoryFilters,
    budget,
    parentBudgetUnit,
    loadingParentUnit,
    fetchParentUnit,
  } = useContext(BudgetDetailContext);
  const { existingCategoryFilters, saveFilterState } = useBudgetFilterStorage(
    budget.id,
  );

  const [categoryFiltersCart, setCategoryFiltersCart] =
    useState<BudgetCategory[]>(categoryFilters);
  const [categoryFiltersSnapshot, setCategoryFiltersSnapshot] =
    useState<BudgetCategory[]>(categoryFilters);

  const [searchOptions, setSearchOptions] = useState<BudgetCategory[]>([]);
  const [searchPage, setSearchPage] = useState<number>(1);
  const [searchTotal, setSearchTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [open, setOpen] = useState<boolean>();
  const [options, setOptions] = useState<BudgetCategory[]>(
    existingCategoryFilters,
  );
  const [query, setQuery] = useState<string>('');

  const searching = useMemo(() => query.length >= MIN_SEARCH_CHARS, [query]);

  const [fetchCategoryOptions, { loading: loadingOptions }] = useLazyQuery<
    { budgetCategoriesFilter: BudgetCategoryFiltersConnection },
    { input: BudgetCategoriesFilterConnectionInput }
  >(GET_BUDGET_CATEGORY_FILTERS, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const nextOptions = R.pathOr(
        [],
        ['budgetCategoriesFilter', 'budgetCategories'],
        data,
      );
      const newTotal = data?.budgetCategoriesFilter?.totalCount || 0;
      if (searching) {
        setSearchOptions([...searchOptions, ...nextOptions]);
        setSearchTotal(newTotal);
      } else {
        setOptions(R.uniqBy(R.prop('id'), [...options, ...nextOptions]));
        setTotal(newTotal);
      }
    },
  });

  const handleSetCategoryCart = (newCategories: BudgetCategory[]) => {
    setCategoryFiltersCart(newCategories);
  };

  const handleSelectOption = (option: BudgetCategory) => {
    const selectedIds = categoryFiltersCart.map(category => category.id);
    const newCategories = selectedIds.includes(option.id)
      ? categoryFiltersCart.filter(category => category.id !== option.id)
      : [...categoryFiltersCart, option];
    handleSetCategoryCart(newCategories);
  };

  const handleClearSelections = () => {
    setCategoryFilters([]);
    handleSetCategoryCart([]);
    saveFilterState(FILTER_KEYS.CATEGORY, []);
    fetchParentUnit({ budgetUnitId: parentBudgetUnit?.id, newCategoryIds: [] });
  };

  const fetchCategoryOptionsDebounced = useCallback(
    debounce((value: string) => {
      fetchCategoryOptions({
        variables: {
          input: {
            budgetId: budget.id,
            name: value,
            limit: LIMIT,
          },
        },
      });
    }, DEBOUNCE_TIME),
    [],
  );

  const handleQueryChange = (value: string = '') => {
    setQuery(value);
    setSearchPage(1);
    setSearchOptions([]);
    if (!value) {
      fetchCategoryOptionsDebounced.cancel();
    } else if (value.length >= MIN_SEARCH_CHARS) {
      fetchCategoryOptionsDebounced(value);
    } else {
      fetchCategoryOptionsDebounced.cancel();
    }
  };

  const handlePageScroll = (nextPage: number) => {
    if (searching) {
      setSearchPage(nextPage);
    } else {
      setPage(nextPage);
    }

    fetchCategoryOptions({
      variables: {
        input: {
          budgetId: budget.id,
          limit: LIMIT,
          page: nextPage,
          name: query,
        },
      },
    });
  };

  useEffect(() => {
    // Cache initial selected item snapshot when user opens menu
    if (open === true) {
      setCategoryFiltersSnapshot(categoryFiltersCart);
    }
    if (
      open === false &&
      !objectIdsMatch(categoryFiltersSnapshot, categoryFiltersCart)
    ) {
      setCategoryFilters(categoryFiltersCart);
      saveFilterState(FILTER_KEYS.CATEGORY, categoryFiltersCart);
      fetchParentUnit({
        budgetUnitId: parentBudgetUnit?.id,
        newCategoryIds: categoryFiltersCart.map(category => category?.id),
      });
    }
  }, [open]);

  useEffect(() => {
    fetchCategoryOptions({
      variables: {
        input: {
          budgetId: budget.id,
          limit: LIMIT,
        },
      },
    });
  }, []);

  return (
    <ComboSelect
      open={open}
      setOpen={setOpen}
      itemName="Category"
      loading={loadingOptions}
      options={searching ? searchOptions : options}
      total={searching ? searchTotal : total}
      page={searching ? searchPage : page}
      handlePageScroll={handlePageScroll}
      query={query}
      onQueryChange={handleQueryChange}
      selectedOptions={categoryFiltersCart}
      clearSelections={() => handleClearSelections()}
      disableClear={loadingParentUnit}
      onSelectOption={handleSelectOption}
      dataCyLabel="BudgetCategoryFilter"
    />
  );
};

export default BudgetDetailCategoryFilter;
